import IntervaloLectura from "./IntervaloLectura";
import { intervaloEnMinutos } from "./intervaloLecturaUtils";
// src/components/Sensores/CrearSensor.jsx
import React, { useEffect, useMemo, useState } from "react";
import GuiaConexion from "../Dispositivos/GuiaConexion";
import sensorService, { SignalType } from "../../services/sensorService";
import { obtenerMisDispositivos } from "../../services/dispositivoService";
import usuarioService from "../../services/usuarioService";
import { recomendarPinesSensor, recomendarTipoSenal } from "./pinRecommendations";

/* Helpers de normalización seguros */
const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data?.data && Array.isArray(data.data)) return data.data;
  if (data?.items && Array.isArray(data.items)) return data.items;
  if (data?.result && Array.isArray(data.result)) return data.result;
  if (data?.usuarios && Array.isArray(data.usuarios)) return data.usuarios;
  return [];
};
const toOptionsArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.opciones)) return data.opciones;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const CrearSensor = ({ initialDispositivoId, onCreated }) => {
  // Datos básicos
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");

  // Dispositivo y tipos
  const [dispositivos, setDispositivos] = useState([]);
  const [dispositivoId, setDispositivoId] = useState(String(initialDispositivoId || new URLSearchParams(window.location.search).get("dispositivoId") || ""));

  const [tiposSensor, setTiposSensor] = useState([]); // catálogo TipoParametro (pH, TDS, etc.)
  const [tipoParametroId, setTipoParametroId] = useState("");

  // Tipo de señal (V2)
  const [signalType, setSignalType] = useState(SignalType.Analog); // ADC1 compatible con Wi-Fi

  // Pines V2
  const [pinPrincipal, setPinPrincipal] = useState("");
  const [pinRx, setPinRx] = useState("");
  const [pinTx, setPinTx] = useState("");

  // Opciones de pines libres (según dispositivo + signalType)
  const [pinesLibres, setPinesLibres] = useState([]); // [{pin, etiqueta, ocupado, riesgoso, motivo}]
  const [cargandoPines, setCargandoPines] = useState(false);

  // Permisos
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosSeleccionados, setUsuariosSeleccionados] = useState([]);

  // Tiempo de lectura (min)
  const [tiempoSensor, setTiempoSensor] = useState(1);
  const [unidadTiempo, setUnidadTiempo] = useState("minutos");

  // Mensajes
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  // UI helpers
  const esUART = useMemo(() => signalType === SignalType.Uart, [signalType]);
  const usaPinPrincipal = useMemo(
    () =>
      signalType === SignalType.Analog ||
      signalType === SignalType.OneWire ||
      signalType === SignalType.Digital ||
      signalType === SignalType.I2c,
    [signalType]
  );
  const tipoSensorSeleccionado = tiposSensor.find(
    (tp) => String(tp.id) === String(tipoParametroId)
  );

  // Cargas iniciales
  useEffect(() => {
    (async () => {
      try {
        const [dispositivosData, tiposSensorData, usuariosData] = await Promise.all([
          obtenerMisDispositivos(),
          sensorService.obtenerTiposSensor(),
          usuarioService.obtenerUsuariosCreadosPorAdmin(),
        ]);
        setDispositivos(toArray(dispositivosData));
        setTiposSensor(toArray(tiposSensorData));
        setUsuarios(toArray(usuariosData)); // ← ¡clave! siempre array
      } catch (err) {
        console.error("Error cargando datos:", err);
        setDispositivos([]);
        setTiposSensor([]);
        setUsuarios([]);
        setError("Error al cargar los datos.");
      }
    })();
  }, []);

  // Cargar pines libres cuando cambian dispositivo o tipo de señal
  useEffect(() => {
    (async () => {
      setPinesLibres([]);
      // Si cambia dispositivo/tipo, resetea selección de pines para evitar inconsistencias
      setPinPrincipal("");
      setPinRx("");
      setPinTx("");

      if (!dispositivoId || signalType == null) return;
      try {
        setCargandoPines(true);
        const data = await sensorService.obtenerPinesLibres(parseInt(dispositivoId, 10), signalType);
        setPinesLibres(toOptionsArray(data));
      } catch (err) {
        console.error("Error obteniendo pines libres:", err);
        setPinesLibres([]);
      } finally {
        setCargandoPines(false);
      }
    })();
  }, [dispositivoId, signalType]);

  const togglePermiso = (id) => {
    setUsuariosSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]
    );
  };

  const limpiarFormulario = () => {
    setNombre("");
    setDescripcion("");
    setDispositivoId(String(initialDispositivoId || new URLSearchParams(window.location.search).get("dispositivoId") || ""));
    setTipoParametroId("");
    setSignalType(SignalType.Uart);
    setPinPrincipal("");
    setPinRx("");
    setPinTx("");
    setUsuariosSeleccionados([]);
    setTiempoSensor(1);
    setUnidadTiempo("minutos");
  };

  // Agrupar opciones de pines (Recomendados / Avanzado)
  const getGroupedOptions = () => {
    const etiquetaHas = (o, txt) =>
      (o?.etiqueta || "").toLowerCase().includes((txt || "").toLowerCase());

    const isRecommended = (o) => {
      switch (signalType) {
        case SignalType.Analog:
          return etiquetaHas(o, "adc1") && !o.riesgoso;
        case SignalType.OneWire:
          return !o.riesgoso && !etiquetaHas(o, "input-only") && !etiquetaHas(o, "boot");
        case SignalType.Digital:
          return !o.riesgoso && !etiquetaHas(o, "input-only");
        case SignalType.Uart:
          return !o.riesgoso && !etiquetaHas(o, "uart_");
        case SignalType.I2c:
          return !o.riesgoso;
        default:
          return !o.riesgoso;
      }
    };

    const recomendados = [];
    const avanzados = [];
    for (const o of pinesLibres) (isRecommended(o) ? recomendados : avanzados).push(o);

    const sortFn = (a, b) => {
      if (a.ocupado !== b.ocupado) return a.ocupado ? 1 : -1; // libres primero
      return (a.pin || "").localeCompare(b.pin || "", "es", { numeric: true });
    };
    recomendados.sort(sortFn);
    avanzados.sort(sortFn);

    return { recomendados, avanzados };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje("");
    setError("");

    const tipoId = parseInt(tipoParametroId, 10);
    const dispositivoParsedId = parseInt(dispositivoId, 10);
    let tiempoParsed;
    try { tiempoParsed = intervaloEnMinutos(tiempoSensor, unidadTiempo); }
    catch (error) { setError(error.message); return; }

    // Validaciones mínimas
    if (!nombre || !dispositivoParsedId || !tipoId) {
      setError("Completa al menos: Nombre, Dispositivo y Tipo de Sensor.");
      return;
    }


    // Validar pines según tipo de señal
    if (esUART) {
      if (!pinRx || !pinTx) {
        setError("Para UART debes seleccionar Pin Rx y Pin Tx.");
        return;
      }
      if (pinRx === pinTx) {
        setError("Pin Rx y Pin Tx no pueden ser el mismo.");
        return;
      }
    } else if (usaPinPrincipal) {
      if (!pinPrincipal) {
        setError("Debes seleccionar un pin válido para este tipo de señal.");
        return;
      }
    }

    // Payload V2 (permisos como **strings**)
    const payloadV2 = {
      nombre: nombre.trim(),
      descripcion: (descripcion || "").trim(),
      dispositivoId: dispositivoParsedId,
      tipoParametroId: tipoId,
      signalType, // int (0..4)
      tiempoSensor: tiempoParsed,
      permisos: (usuariosSeleccionados || []).map(String),
      pinPrincipal: usaPinPrincipal ? pinPrincipal : undefined,
      pinRx: esUART ? pinRx : undefined,
      pinTx: esUART ? pinTx : undefined,
    };

    try {
      setGuardando(true);
      const resp = await sensorService.crearSensorV2(payloadV2);
      onCreated?.(resp);
      const sensorId = resp?.sensorId ?? resp?.id;

      // Compatibilidad: el endpoint V2 ya guarda la frecuencia. Este bloque
      // sólo cubre backends antiguos y acepta el conflicto si ya existe.
      if (sensorId && tiempoParsed) {
        try {
          await sensorService.crearTiempoSensor({
            controladorId: sensorId,
            dispositivoId: dispositivoParsedId,
            tiempoSensor: tiempoParsed,
          });
        } catch (err) {
          // Si no es superadmin, el backend devuelve 403 -> informamos pero NO rompemos
          if (err?.response?.status === 403) {
            setMensaje("✅ Sensor creado. ℹ️ Frecuencia no guardada: permisos insuficientes.");
          } else if (err?.response?.status !== 409) {
            // 409/Conflict u otros “esperables” no bloquean; cualquier otro, lo mostramos suave
            console.error("Error creando config de tiempo:", err);
            setMensaje("✅ Sensor creado. ℹ️ No se pudo guardar la frecuencia.");
          }
        }
      } else {
        setMensaje("✅ Sensor creado exitosamente.");
      }

      if (!mensaje) setMensaje("✅ Sensor creado exitosamente.");
      limpiarFormulario();
    } catch (err) {
      console.error("Error al crear el sensor:", err);
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 401) setError("No autorizado. Vuelve a iniciar sesión.");
      else if (status === 403) setError("No tienes permisos sobre ese dispositivo.");
      else if ((status === 400 || status === 409) && data) {
        if (typeof data === "string") setError(data);
        else if (data.errors) setError(Object.values(data.errors).flat().join(" | "));
        else if (data.message) setError(data.message);
        else if (data.title) setError(data.title);
        else setError(JSON.stringify(data));
      } else setError("Error al crear el sensor.");
    } finally {
      setGuardando(false);
    }
  };

  // Select dinámico de pines
  const renderSelectPines = () => {
    const tipoSeleccionado = tiposSensor.find((tp) => String(tp.id) === String(tipoParametroId));
    const recomendacionPines = recomendarPinesSensor(tipoSeleccionado?.nombre, signalType);
    if (cargandoPines) return <p className="text-neutral-300 text-sm">Cargando pines…</p>;

    const { recomendados, avanzados } = getGroupedOptions();
    const renderOpts = (lista) =>
      lista.map((o) => (
        <option key={o.pin} value={o.pin} disabled={o.ocupado}>
          {o.etiqueta || o.pin}
          {o.ocupado ? ` (ocupado: ${o.motivo || "otro componente"})` : ""}
          {o.riesgoso ? " (riesgoso)" : ""}
        </option>
      ));

    const NotaTipo = () => {
      if (signalType === SignalType.Analog)
        return (
          <p className="text-xs text-neutral-300 mt-2">
            Sugerencia: usa <b>ADC1 (GPIO32, 33, 34, 35, 36 o 39)</b>. Evita ADC2 porque Wi-Fi lo pisa.
          </p>
        );
      if (signalType === SignalType.OneWire)
        return (
          <p className="text-xs text-neutral-300 mt-2">
            DS18B20 requiere <b>pull-up 4.7&nbsp;kΩ</b> entre DATA y 3V3.
          </p>
        );
      return null;
    };

    if (esUART) {
      return (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white mb-1 text-sm sm:text-base">Pin Rx</label>
              <select
                value={pinRx}
                onChange={(e) => setPinRx(e.target.value)}
                title={recomendacionPines}
                className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
              >
                <option value="">Seleccione</option>
                {recomendados.length > 0 && (
                  <optgroup label="Recomendados">
                    {recomendados
                      .filter((o) => o.pin !== pinTx)
                      .map((o) => (
                        <option key={o.pin} value={o.pin} disabled={o.ocupado}>
                          {o.etiqueta || o.pin}
                          {o.ocupado ? ` (ocupado: ${o.motivo || "otro componente"})` : ""}
                        </option>
                      ))}
                  </optgroup>
                )}
                {avanzados.length > 0 && (
                  <optgroup label="Avanzado">
                    {avanzados
                      .filter((o) => o.pin !== pinTx)
                      .map((o) => (
                        <option key={o.pin} value={o.pin} disabled={o.ocupado}>
                          {o.etiqueta || o.pin}
                          {o.ocupado ? ` (ocupado: ${o.motivo || "otro componente"})` : ""}
                          {o.riesgoso ? " (riesgoso)" : ""}
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            </div>

            <div>
              <label className="block text-white mb-1 text-sm sm:text-base">Pin Tx</label>
              <select
                value={pinTx}
                onChange={(e) => setPinTx(e.target.value)}
                title={recomendacionPines}
                className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
              >
                <option value="">Seleccione</option>
                {recomendados.length > 0 && (
                  <optgroup label="Recomendados">
                    {recomendados
                      .filter((o) => o.pin !== pinRx)
                      .map((o) => (
                        <option key={o.pin} value={o.pin} disabled={o.ocupado}>
                          {o.etiqueta || o.pin}
                          {o.ocupado ? ` (ocupado: ${o.motivo || "otro componente"})` : ""}
                        </option>
                      ))}
                  </optgroup>
                )}
                {avanzados.length > 0 && (
                  <optgroup label="Avanzado">
                    {avanzados
                      .filter((o) => o.pin !== pinRx)
                      .map((o) => (
                        <option key={o.pin} value={o.pin} disabled={o.ocupado}>
                          {o.etiqueta || o.pin}
                          {o.ocupado ? ` (ocupado: ${o.motivo || "otro componente"})` : ""}
                          {o.riesgoso ? " (riesgoso)" : ""}
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            </div>
          </div>
          <p className="mt-2 text-sm text-neutral-200">{recomendacionPines}</p>
          <NotaTipo />
        </>
      );
    }

    if (usaPinPrincipal) {
      return (
        <div>
          <label className="block text-white mb-1 text-sm sm:text-base">GPIO de señal del ESP32 (AO, DO o DATA)</label>
          <select
            value={pinPrincipal}
            onChange={(e) => setPinPrincipal(e.target.value)}
            title={recomendacionPines}
            className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
          >
            <option value="">Seleccione</option>
            {recomendados.length > 0 && (
              <optgroup label="Recomendados">{renderOpts(recomendados)}</optgroup>
            )}
            {avanzados.length > 0 && (
              <optgroup label="Avanzado">{renderOpts(avanzados)}</optgroup>
            )}
          </select>
          <p className="mt-2 text-sm text-neutral-200">{recomendacionPines}</p>
          <NotaTipo />
        </div>
      );
    }

    return null;
  };

  return (
    <div className="bg-neutral-800 p-4 sm:p-6 rounded-lg shadow-md w-full max-w-lg sm:max-w-xl md:max-w-2xl mx-auto">
      <h3 className="text-green-400 text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
        Crear Nuevo Sensor
      </h3>

      <GuiaConexion pin={pinPrincipal} rx={pinRx} tx={pinTx} />
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white mb-1 text-sm sm:text-base">Nombre del Sensor</label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            />
          </div>

          <div className="md:col-span-1">
            <label className="block text-white mb-1 text-sm sm:text-base">Descripción</label>
            <input
              type="text"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            />
          </div>

          <div>
            <label className="block text-white mb-1 text-sm sm:text-base">Dispositivo</label>
            <select
              value={dispositivoId}
              onChange={(e) => setDispositivoId(e.target.value)}
              className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            >
              <option value="">Seleccione</option>
              {dispositivos.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-white mb-1 text-sm sm:text-base">Tipo de Sensor</label>
            <select
              value={tipoParametroId}
              onChange={(e) => setTipoParametroId(e.target.value)}
              className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            >
              <option value="">Seleccione</option>
              {tiposSensor.map((tp) => (
                <option key={tp.id} value={tp.id}>
                  {tp.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-white mb-1 text-sm sm:text-base">Tipo de señal</label>
            <select
              value={signalType}
              onChange={(e) => setSignalType(parseInt(e.target.value, 10))}
              title={recomendarTipoSenal(tipoSensorSeleccionado?.nombre)}
              className="w-full p-2 sm:p-3 rounded bg-neutral-700 text-white"
            >
              <option value={SignalType.Analog}>Analógica (ADC)</option>
              <option value={SignalType.OneWire}>1-Wire</option>
              <option value={SignalType.Digital}>Digital</option>
              <option value={SignalType.Uart} disabled>UART — no disponible</option>
              <option value={SignalType.I2c} disabled>I²C — no disponible</option>
            </select>
          </div>

          <div className="md:col-span-2">{renderSelectPines()}</div>

          <div>
            <IntervaloLectura cantidad={tiempoSensor} unidad={unidadTiempo}
              onCantidadChange={setTiempoSensor} onUnidadChange={setUnidadTiempo} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-white mb-2 text-sm sm:text-base">
              Permisos (usuarios que podrán ver este sensor)
            </label>
            <div className="max-h-40 sm:max-h-60 overflow-y-auto border border-neutral-600 p-2 rounded bg-neutral-900">
              {usuarios.length > 0 ? (
                usuarios.map((u) => (
                  <label key={u.id} className="flex items-center text-white text-sm sm:text-base py-0.5">
                    <input
                      type="checkbox"
                      checked={usuariosSeleccionados.includes(u.id)}
                      onChange={() => togglePermiso(u.id)}
                      className="mr-2"
                    />
                    {u.nombre} ({u.email})
                  </label>
                ))
              ) : (
                <p className="text-neutral-400 text-sm">No hay usuarios temporales disponibles.</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={guardando}
            className={`w-full sm:w-auto text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded ${
              guardando ? "bg-gray-600" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {guardando ? "Guardando..." : "Guardar Sensor"}
          </button>
        </div>
      </form>

      {mensaje && <p className="text-green-400 mt-4 text-sm sm:text-base">{mensaje}</p>}
      {error && <p className="text-red-400 mt-4 text-sm sm:text-base">{error}</p>}
    </div>
  );
};

export default CrearSensor;
